import { captureError } from "@/shared/services/sentry";

import type {
  DeleteMessageParams,
  GetMessagesParams,
  SendMessageParams,
  SendMessageWithMediaParams,
  SendVoiceMessageParams
} from "@/features/chat/domain/repositories/message";

import type { HttpGateway } from "@/shared/services/http";

export const messagingDataSourceImplementation = (gateway: HttpGateway) => ({
  getMessages: async <T>(params: GetMessagesParams): Promise<T> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cursor) queryParams.append("cursor", params.cursor);
      if (params.limit) queryParams.append("limit", params.limit.toString());

      const queryString = queryParams.toString();
      const endpoint = `/v1/chats/${params.userId}${
        queryString ? `?${queryString}` : ""
      }`;
      return await gateway.get(endpoint, undefined, {
        signal: params.signal
      });
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        throw e;
      }
      captureError(e, "chat", "getMessages", { params }, "messaging");
      const error = e as Error;
      throw new Error(
        `Error while fetching messages from /v1/chats/${params.userId}: ${error.message}`,
        { cause: e }
      );
    }
  },

  sendMessage: async <T>(
    params: SendMessageParams | SendMessageWithMediaParams
  ): Promise<T> => {
    try {
      const endpoint = `/v1/chats/messages`;
      const body: Record<string, unknown> = {
        receiver_id: params.receiverId,
        content: params.content,
        type: params.type ?? "text"
      };
      const mediaParams = params as SendMessageWithMediaParams;
      if (mediaParams.attachments) {
        body.type = "private_media";
        body.price = mediaParams.attachments[0].price;
        body.attachments = mediaParams.attachments[0].mediaIds;
        body.third_party_consent = mediaParams.thirdPartyConsent;
      }

      return await gateway.post(endpoint, body);
    } catch (e) {
      captureError(e, "chat", "sendMessage", { params }, "messaging");
      const error = e as Error;
      throw new Error(
        `Error while sending message to /v1/chats/messages: ${error.message}`,
        { cause: e }
      );
    }
  },

  sendVoiceMessage: async <T>(params: SendVoiceMessageParams): Promise<T> => {
    const { receiverId, mediaId, idempotencyKey, signal } = params;
    try {
      return await gateway.post(
        "/v2/chats/messages",
        {
          receiver_id: receiverId,
          type: "private_media",
          private_media: {
            price: 0,
            media_ids: [mediaId]
          }
        },
        {
          headers: { "Idempotency-Key": idempotencyKey },
          signal
        }
      );
    } catch (e) {
      if (
        e instanceof Error &&
        (e.name === "AbortError" || e.name === "CanceledError")
      ) {
        throw e;
      }
      captureError(
        e,
        "chat",
        "sendVoiceMessage",
        { receiverId, mediaId },
        "messaging"
      );
      throw new Error(
        `Error while sending voice message to /v2/chats/messages: ${
          (e as Error).message
        }`,
        { cause: e }
      );
    }
  },

  getMessageMedia: async <T>(messageIds: string[]): Promise<T> => {
    try {
      const endpoint = `/v1/chats/messages?ids=${messageIds.join(",")}`;
      return await gateway.get(endpoint);
    } catch (e) {
      captureError(e, "chat", "getMessageMedia", { messageIds }, "messaging");
      const error = e as Error;
      throw new Error(
        `Error while fetching message media from /v1/chats/messages: ${error.message}`,
        { cause: e }
      );
    }
  },

  deleteMessage: async <T>(params: DeleteMessageParams): Promise<T> => {
    try {
      const endpoint = `/v1/chats/messages/${params.messageId}?for=${params.deleteFor}`;
      return await gateway.delete(endpoint);
    } catch (e) {
      captureError(e, "chat", "deleteMessage", { params }, "messaging");
      const error = e as Error;
      throw new Error(
        `Error while deleting message /v1/chats/messages/${params.messageId}: ${error.message}`,
        { cause: e }
      );
    }
  }
});
